import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export default function PersonalInformationSection({ data, onChange, onAddDependent, onRemoveDependent, isOpen, onOpen, }) {
  const [collapsed, setCollapsed] = useState(false);
  const isMarried = data.maritalStatus === "married";
  const isSpouseFiling = isMarried && data.spouseFiling === "yes";
  const showStatusDuration = ["divorced", "separated", "widowed"].includes(data.maritalStatus);

  return (
    <section className="space-y-4">
      {/* Toggle Header */}
      <div className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded shadow-xl cursor-pointer" onClick={onOpen}>
        <h2 className="text-lg font-semibold text-gray-800">Personal Information</h2>
        {isOpen ? <ChevronRightIcon className="w-5 h-5 text-gray-600" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" />}
      </div>

      {isOpen && (
        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name + DOB */}
            <div className="bg-white shadow-xl rounded p-3 border">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Name & DOB</h3>
              <div className="grid grid-cols-2 gap-2">
                <input name="firstName" value={data.firstName} onChange={onChange} placeholder="First Name" className="input" />
                <input name="middleName" value={data.middleName} onChange={onChange} placeholder="Middle Name" className="input" />
                <input name="lastName" value={data.lastName} onChange={onChange} placeholder="Last Name" className="input" />
                <input type="date" name="dob" value={data.dob} onChange={onChange} className="input" />
              </div>
            </div>

            {/* Marital Status */}
            <div className="bg-white shadow-xl rounded p-3 border">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Marital Status</h3>
              <select name="maritalStatus" value={data.maritalStatus} onChange={onChange} className="input mb-2">
                <option value="">Select Status</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="separated">Separated</option>
                <option value="widowed">Widowed</option>
                <option value="single">Single</option>
              </select>
              {isMarried && (
                <>
                  <input name="yearsMarried" value={data.yearsMarried} onChange={onChange} placeholder="Years Married" className="input mb-2" />
                  <select name="spouseFiling" value={data.spouseFiling} onChange={onChange} className="input">
                    <option value="">Is spouse filing?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </>
              )}
              {showStatusDuration && (
                <input name="statusDuration" value={data.statusDuration} onChange={onChange} placeholder="How long?" className="input mt-2" />
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="bg-white shadow-xl rounded p-3 border">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Prior Bankruptcy</h3>
            <select name="bankruptcyFiled" value={data.bankruptcyFiled} onChange={onChange} className="input mb-2">
              <option value="">Have you filed before?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {data.bankruptcyFiled === 'yes' && (
              <div className="grid grid-cols-2 gap-2">
              <select 
                name="bankruptcyChapter" 
                value={data.bankruptcyChapter} 
                onChange={onChange} 
                className="input"
              >
                <option value="">Chapter</option>
                <option value="7">Chapter 7</option>
                <option value="13">Chapter 13</option>
              </select>
            
              <select 
                name="bankruptcyYearsAgo" 
                value={data.bankruptcyYearsAgo} 
                onChange={onChange} 
                className="input"
              >
                <option value="">Years ago</option>
                {[...Array(30)].map((_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            )}
          </div>

          {/* Row 3 (Spouse Info) */}
          {isSpouseFiling && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white shadow-xl rounded p-3 border">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Spouse Name & DOB</h3>
                <div className="grid grid-cols-2 gap-2">
                  <input name="spouseFirstName" value={data.spouseFirstName} onChange={onChange} placeholder="First Name" className="input" />
                  <input name="spouseMiddleName" value={data.spouseMiddleName} onChange={onChange} placeholder="Middle Name" className="input" />
                  <input name="spouseLastName" value={data.spouseLastName} onChange={onChange} placeholder="Last Name" className="input" />
                  <input type="date" name="spouseDOB" value={data.spouseDOB} onChange={onChange} className="input" />
                </div>
              </div>

              <div className="bg-white shadow-xl rounded p-3 border">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Spouse Bankruptcy</h3>
                <select name="spouseBankruptcyFiled" value={data.spouseBankruptcyFiled} onChange={onChange} className="input mb-2">
                  <option value="">Filed before?</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                {data.spouseBankruptcyFiled === 'yes' && (
                  <div className="grid grid-cols-2 gap-2">
                    <select name="spouseBankruptcyChapter" value={data.spouseBankruptcyChapter} onChange={onChange} className="input">
                      <option value="">Chapter</option>
                      <option value="7">Chapter 7</option>
                      <option value="13">Chapter 13</option>
                    </select>
                    <input name="spouseBankruptcyYearsAgo" value={data.spouseBankruptcyYearsAgo} onChange={onChange} placeholder="Years ago" className="input" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Row 4 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white shadow-xl rounded p-3 border">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Address</h3>
              <input name="street" value={data.street} onChange={onChange} placeholder="Street" className="input mb-2" />
              <div className="grid grid-cols-2 gap-2">
                <input name="city" value={data.city} onChange={onChange} placeholder="City" className="input" />
                <input name="state" value={data.state} onChange={onChange} placeholder="State" className="input" />
              </div>
              <input name="zip" value={data.zip} onChange={onChange} placeholder="ZIP" className="input mt-2" />
            </div>

            <div className="bg-white shadow-xl rounded p-3 border">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Contact</h3>
              <div className="grid grid-cols-2 gap-2">
                <input name="phone" value={data.phone} onChange={onChange} placeholder="Phone" className="input" />
                <input name="email" value={data.email} onChange={onChange} placeholder="Email" className="input" />
                {isSpouseFiling && (
                  <>
                    <input name="spousePhone" value={data.spousePhone} onChange={onChange} placeholder="Spouse Phone" className="input" />
                    <input name="spouseEmail" value={data.spouseEmail} onChange={onChange} placeholder="Spouse Email" className="input" />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Row 5: Dependents */}
          <div className="bg-white shadow-xl rounded p-3 border">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Dependents</h3>
            <div className="space-y-2">
              {data.dependentDetails.map((dep, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-1 sm:grid-cols-${dep.relation === 'other' ? '4' : '3'} gap-2 items-end`}
                >
                  <input
                    value={dep.age}
                    onChange={e => {
                      const updated = [...data.dependentDetails];
                      updated[i].age = e.target.value;
                      onChange({ target: { name: "dependentDetails", value: updated } });
                    }}
                    placeholder="Age"
                    className="input"
                  />
                  <select
                    value={dep.relation}
                    onChange={e => {
                      const updated = [...data.dependentDetails];
                      updated[i].relation = e.target.value;
                      onChange({ target: { name: "dependentDetails", value: updated } });
                    }}
                    className="input"
                  >
                    <option value="">Relation</option>
                    <option value="son">Son</option>
                    <option value="daughter">Daughter</option>
                    <option value="mother">Mother</option>
                    <option value="father">Father</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="grandchild">Grandchild</option>
                    <option value="other">Other</option>
                  </select>
                  {dep.relation === 'other' && (
                    <input
                      value={dep.relationOther || ''}
                      onChange={e => {
                        const updated = [...data.dependentDetails];
                        updated[i].relationOther = e.target.value;
                        onChange({ target: { name: "dependentDetails", value: updated } });
                      }}
                      placeholder="Please specify"
                      className="input"
                    />
                  )}
                  <button type="button" onClick={() => onRemoveDependent(i)} className="text-sm text-red-600">Remove</button>
                </div>
              ))}
              <button type="button" onClick={onAddDependent} className="text-sm text-blue-600 mt-2">+ Add Dependent</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}